import _ from 'lodash';
import React from 'react';
import styled from 'styled-components';
import Truncate from 'react-truncate';
import { DEFAULT_SPECIALS_CARD_IMG } from 'shared/constants';
import { replaceAWSSourceWithImgix } from 'shared/helpers/imgix';

function OffersCarouselCard({
  className = `offers`,
  displayImage,
  displayName,
  enableRightMargin = true,
  handleClick,
  isSingularCard,
  specialId,
  ...props
}) {
  return (
    <AspectRatioOuter className={className} enableRightMargin={enableRightMargin} isSingularCard={isSingularCard}>
      <AspectRatioInner>
        <OffersCardContainer
          className={className}
          data-cy='offers-carousel-card'
          data-test='offers-carousel-card'
          imageUrl={replaceAWSSourceWithImgix(displayImage) || DEFAULT_SPECIALS_CARD_IMG}
          isSingularCard={isSingularCard}
          onClick={() => handleClick(specialId)}
          {...props}
        >
          <TextContainer>
            <Title>
              <Truncate lines={2}>{displayName}</Truncate>
            </Title>
          </TextContainer>
          <ActionsContainer>
            <ShopButton onClick={() => handleClick(specialId)}>SHOP</ShopButton>
          </ActionsContainer>
        </OffersCardContainer>
      </AspectRatioInner>
    </AspectRatioOuter>
  );
}

const ShopButton = styled.button`
  width: 65px;
  height: 32px;
  background: transparent;
  font-weight: bold;
  font-size: 12px;
  line-height: 13px;
  text-transform: uppercase;
  color: #ffffff;
  border: 2px solid #ffffff;
  border-radius: 20px;

  &:hover {
    cursor: pointer;
  }
`;

const ActionsContainer = styled.div``;

const Title = styled.p`
  font-weight: bold;
  font-size: 16px;
  line-height: 120%;
  color: #ffffff;
  text-shadow: 0px 2px 4px rgba(152, 163, 173, 0.36);
  width: 100%;
  word-break: break-word;
`;

const TextContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 24px;
  max-width: 232px;
`;

const AspectRatioOuter = styled.div`
  border-radius: 20px;
  position: relative;
  width: 100%;
  min-width: 310px; /* this is our initial multi offer card size */
  margin-right: 14px;
  scroll-margin: 14px;
  scroll-snap-align: start;
  scroll-snap-stop: normal;

  /* only turn off the last margin if we have more cards than our row size */
  &.last-card {
    /* give us enough room to let our box shadows end */
    &:after {
      content: '';
      display: block;
      background: transparent;
      position: absolute;
      right: -14px;
      top: 0px;
      bottom: 0;
      left: 100%;
    }
  }

  /* up until 600px.... */
  @media (max-width: 600px) {
    ${({ className, isSingularCard }) =>
      /* single card offers can stretch full width up to 600 */
      _.includes(className, `offers`) &&
      isSingularCard &&
      `
      min-width: unset;
      margin-right: 25px;
    `}
  }

  /* over 600px.... */
  @media (min-width: 600px) {
    /* allows us to fit 2 across */
    min-width: 260.25px; /* allows for 14px of space in between */
    /* REMOVE NEXT LINE IF WE EVER WANT TO STOP HAVING LOADS OF EXTRA SPACE */
    width: 260.25px;

    &.last-card {
      /* give us enough room to let our box shadows end */
      &:after {
        right: ${({ enableRightMargin }) => (enableRightMargin ? `-14px` : 0)};
      }
    }
  }

  /* over 750.5px.... */
  @media (min-width: 750.5px) {
    /* allows us to fit 3 across */
    min-width: 240.833px; /* allows for 14px of space in between */
    /* REMOVE NEXT LINE IF WE EVER WANT TO STOP HAVING LOADS OF EXTRA SPACE */
    width: 240.833px;
  }

  /* over 966.5px.... */
  @media (min-width: 966.5px) {
    /* allows us to fit 3 across */
    min-width: 312.833px; /* allows for 14px of space in between */
    /* REMOVE NEXT LINE IF WE EVER WANT TO STOP HAVING LOADS OF EXTRA SPACE */
    width: 312.833px;
  }

  /* over 1187px.... */
  @media (min-width: 1187px) {
    /* allows us to fit 3 across */
    min-width: 377.5px; /* this is our magic number from figma, but will allow for more space (27.25px) */
    /* REMOVE NEXT LINE IF WE EVER WANT TO STOP HAVING LOADS OF EXTRA SPACE */
    width: 377.5px;
    margin-right: 27px;
    /* turn on the gradients! */
    box-shadow: 0px 4px 14px rgba(0, 0, 0, 0.25);

    &.last-card {
      /* give us enough room to let our box shadows end*/
      &:after {
        content: '';
        display: block;
        background: transparent;
        position: absolute;
        right: ${({ enableRightMargin }) => (enableRightMargin ? `-27px` : 0)};
        top: 0px;
        bottom: 0;
        left: 100%;
      }
    }
  }
`;

const AspectRatioInner = styled.div`
  position: relative;
  padding-bottom: 50%;
  border-radius: 20px;
  /* over 600px.... */
  @media (min-width: 600px) {
    /* squeezing two cards is so hard here at 2:1 or narrow ratio,
       ima bump this up so that we can fit 2 lines of text */
    padding-bottom: 58%;
  }

  /* over 750.5px.... */
  @media (min-width: 750.5px) {
    padding-bottom: 63%;
  }

  /* over 966.5px.... */
  @media (min-width: 966.5px) {
    padding-bottom: 50%;
  }

  /* over 1187px.... */
  @media (min-width: 1187px) {
    padding-bottom: 43.77%;
  }
`;

const OffersCardContainer = styled.div`
  padding: 29px;
  background: ${({ imageUrl }) =>
    `linear-gradient(180deg, rgba(11, 31, 50, 0.432) 33.75%, rgba(11, 31, 50, 0.9) 100%), url(${imageUrl})`};
  background-position: center left;
  background-size: cover;
  border-radius: 20px;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  &:hover {
    cursor: pointer;
    button {
      border: 2px solid #fff;
      background-color: #fff;
      color: #242526;
    }
  }
`;

export default OffersCarouselCard;
